import { useState } from 'react';
import { Share2, Link2, X, Repeat2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { copyToClipboard } from '@/lib/shareUtil';

export function ShareMenu({ url, title, postId, className = '' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const shareUrl = url || window.location.href;
  const shareTitle = title || 'Check this out on UniVerse!';

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  const close = () => setOpen(false);

  const shareToFeed = async () => {
    if (!user) {
      toast.error('Sign in to share to your feed');
      close();
      return;
    }
    try {
      if (postId) {
        await axios.post(`${API}/posts/${postId}/repost`, { quote: '' });
        toast.success('Shared to your feed!');
      } else {
        await axios.post(`${API}/posts`, {
          content: `${shareTitle}\n${shareUrl}`,
          tags: ['shared'],
        });
        toast.success('Shared to your feed!');
      }
    } catch {
      toast.error('Failed to share');
    }
    close();
  };

  const shareExternal = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') copyLink();
      }
    } else {
      copyLink();
    }
    close();
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) toast.success('Link copied!');
    else toast.error('Could not copy — please copy manually');
    close();
  };

  return (
    <div className={className} onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
      <button
        onClick={handleOpen}
        className="flex items-center justify-center w-9 h-9 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all active:scale-90"
        data-testid="share-btn"
      >
        <Share2 className="h-5 w-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[999]" onClick={close} onTouchEnd={close}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 pb-8 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            data-testid="share-sheet"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-[#0A2540]">Share</span>
              <button onClick={close} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-2">
              <button
                onClick={shareToFeed}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 hover:bg-blue-100 transition-all active:scale-[0.98] text-left"
                data-testid="share-to-feed"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Repeat2 className="h-5 w-5 text-[#2F80ED]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0A2540]">Repost to your Feed</p>
                  <p className="text-caption text-[#2F80ED]">Share as a repost on your timeline</p>
                </div>
              </button>

              <button
                onClick={shareExternal}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all active:scale-[0.98] text-left"
                data-testid="share-external"
              >
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                  <Share2 className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0A2540]">Share via...</p>
                  <p className="text-caption text-[#65676B]">WhatsApp, Twitter, Messages & more</p>
                </div>
              </button>

              <button
                onClick={copyLink}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all active:scale-[0.98] text-left"
                data-testid="share-copy-link"
              >
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                  <Link2 className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0A2540]">Copy Link</p>
                  <p className="text-caption text-[#65676B]">Copy to clipboard</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
